import { useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@eriko/ui/src/components/ui/card';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { useTaxSettings, useUpdateTaxSettings } from '../../../../hooks/useTax';
// Toast on save success/error is handled in useUpdateTaxSettings hook (useTax.ts)

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin',
  WY: 'Wyoming',
};
const US_STATES = Object.keys(STATE_NAMES);

interface TaxSettingsData {
  ein: string | null;
  stateOfIncorporation: string | null;
  accountingBasis: string | null;
}

export function UsTaxSettings() {
  const { data: settings, isLoading } = useTaxSettings();

  if (isLoading || !settings) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return <UsTaxSettingsForm settings={settings} />;
}

/** Display an EIN as the standard XX-XXXXXXX; state stores raw digits. */
function formatEin(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 9);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
}

function UsTaxSettingsForm({ settings }: { settings: TaxSettingsData }) {
  const updateSettings = useUpdateTaxSettings();
  const [ein, setEin] = useState(settings.ein ?? '');
  const [state, setState] = useState(settings.stateOfIncorporation ?? '');
  const [basis, setBasis] = useState(settings.accountingBasis ?? '');

  const hasChanges =
    ein !== (settings.ein ?? '') ||
    state !== (settings.stateOfIncorporation ?? '') ||
    basis !== (settings.accountingBasis ?? '');

  function handleSave() {
    updateSettings.mutate({
      ein: ein || null,
      stateOfIncorporation: state || null,
      accountingBasis: basis || null,
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax Identity</CardTitle>
          <CardDescription>
            Your EIN and state are used for tax estimates and Schedule C reporting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ein">EIN (Employer Identification Number)</Label>
              <Input
                id="ein"
                value={formatEin(ein)}
                onChange={(e) => setEin(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder="XX-XXXXXXX"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">Employer Identification Number (XX-XXXXXXX)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State of Incorporation</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger id="state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{STATE_NAMES[s]} ({s})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="basis">Accounting Basis</Label>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger id="basis" className="w-48">
                <SelectValue placeholder="Select basis" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash basis</SelectItem>
                <SelectItem value="accruals">Accrual basis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateSettings.isPending}
          className="h-10 px-6 text-sm font-semibold"
        >
          {updateSettings.isPending ? 'Saving...' : 'Save tax settings'}
        </Button>
      </div>
    </div>
  );
}
